import React, {useState, useEffect, useRef, useMemo} from 'react'
import {Form, Space, PreviewGroup, Modal, Tag} from '@bedrock/components'
import {Play} from '@bedrock/icons-react'
import {useMemoizedFn} from 'ahooks'
import {useOpenModal} from "#utils/openModal";
import DescriptionsFormItem from "#components/DescriptionsFormItem";
import classNames from 'classnames/bind'
import styles from './index.less'
import TaxRateInputNumber from "#features/NewPendingQuote/TaxRate";
import {currencyOptions} from "#utils/number";
import {SupplierListData} from "#features/SupplierQuotationTable";
import DiscountAmount from "#features/NewPendingQuote/DiscountAmount";
import QuoteFileDetail from "#features/NewPendingQuote/QuoteFileDetail";
import { $sType } from '#utils';
import AiTag, { formatAiPercent } from '#components/AiTag';

const cx = classNames.bind(styles)

type QuotationDetailModalProps = {
    detail?: {
        is_bind_execution_data: boolean;
        demand_suppliers: SupplierListData[];
    } & any;
    index_id?: number;
    cooperate_account_id?: number;
    demand_supplier_id?: number;
}

// 货币类型的key属性
const curryList = [
    'quote_price_no_tax',
    'quote_price_with_tax',
    'discount_amount',
    'discount_quote_price_no_tax',
    'discount_quote_price_with_tax',
    'quote_price',
    'last_round_price',
]

export default function useQuotationDetailModal(props: QuotationDetailModalProps) {

    const [config, setConfig] = useState<QuotationDetailModalProps>(props)
    const [form] = Form.useForm();

    const {detail, cooperate_account_id, demand_supplier_id, index_id = 0} = config;

    const showData = useMemo(() => {
        const supplier = detail?.demand_suppliers?.find(item => item.id === demand_supplier_id)
        const quoteObj = supplier?.project_supplier_quotes?.find(item => item.cooperate_account_id === cooperate_account_id)
        if (!quoteObj) {
            form.setFieldsValue({})
            return
        }
        quoteObj.discount_amount = quoteObj.quote_price_with_tax - quoteObj.discount_quote_price_with_tax;
        let currency = supplier?.currency?.toLowerCase?.() || ''
        if ($sType === 'outer') {
            currency = detail?.quote_currency?.toLowerCase?.() || ''
        }
        if (quoteObj.current_price_change === "Y") {
            quoteObj.$price_change = true;
            const lastRoundQuote = supplier.last_round_quotes?.find(item => item.cooperate_account_id === cooperate_account_id);
            quoteObj.$last_round_quote_price = {
                value: lastRoundQuote?.quote_price,
                currency,
            };
        }

        curryList.forEach(key => {
            quoteObj['$'+key] = {
                value: quoteObj[key],
                currency,
            }
        })
        const showData = {
            ...supplier,
            currency,
            quote: quoteObj,
        }
        return showData
    }, [detail, cooperate_account_id, demand_supplier_id])

    useEffect(() => {
        if (!showData) {
            form.resetFields();
        } else {
            form.setFieldsValue(showData);
        }
    }, [showData])

    const bindData = useMemo(() => {
        return !!detail?.is_bind_execution_data
    }, [detail])

    const isMinPrice = useMemo(() => {
        const quote = showData?.quote;
        if (!quote?.quote_price || !detail?.demand_suppliers?.length) return false;
        const allPrices = detail.demand_suppliers
            .map((s: any) =>
                s.project_supplier_quotes?.find(
                    (q: any) => q.cooperate_account_id === cooperate_account_id,
                )?.quote_price,
            )
            .filter((p: any) => p != null && p > 0);
        if (!allPrices.length) return false;
        const minPrice = Math.min(...allPrices);
        return quote.quote_price === minPrice;
    }, [showData, detail, cooperate_account_id]);

    const modalTitle = useMemo(() => {
        const quote = showData?.quote;
        return (
            <Space size={8} align="center">
                <span>{`合作内容${index_id || ''}-报价详情`}</span>
                {quote?.quote_contain_ai && (
                    <AiTag variant="filled" size="xSmall" text="报价含AI" />
                )}
                {quote?.quote_contain_ai && quote?.ai_penetration_rate != null && (
                    <AiTag
                        variant="outlined"
                        size="xSmall"
                        text={`渗透率 ${formatAiPercent(quote.ai_penetration_rate)}`}
                    />
                )}
                {isMinPrice && (
                    <Tag size="xSmall" color="light-green" text="最低价" />
                )}
            </Space>
        );
    }, [index_id, showData, isMinPrice]);

    const {
        open, destroy
    } = useOpenModal({
        title: modalTitle,
        width: 1000,
        onOk: () => {},
        okButton: false,
        cancelButton: false,
        children: <div>
            <Form form={form} layout="vertical" initialValues={{...config}}>
                <DescriptionsFormItem
                    title=""
                    column={2}
                    items={[
                        {
                            key: 'quote_type',
                            label: '报价类型',
                            formItemKey: 'quote_type',
                            componentClass: 'radio',
                            view: true,
                            required: true,
                            show: !bindData,
                            elementProps: {
                                options: [
                                    {
                                        name: '不含税金额',
                                        value: 0
                                    },
                                    {
                                        name: '含税金额',
                                        value: 1
                                    },
                                ]
                            }
                        },
                        {
                            key: 'tax_rate',
                            label: '税率',
                            formItemKey: 'tax_rate',
                            view: true,
                            componentClass: 'inputNumber',
                            span: bindData ? 2 : 1,
                            required: true,
                            render: () => {
                                return <TaxRateInputNumber
                                    style={{ width: detail?.is_bind_execution_data ? '40%' : '100%' }}
                                    view={true}
                                />
                            }
                        },
                        {
                            key: 'currency',
                            label: '报价币种 ',
                            formItemKey: 'currency',
                            view: true,
                            componentClass: 'select',
                            required: true,
                            span: 2,
                            show: !bindData,
                            elementProps: {
                                options: currencyOptions,
                            }
                        },
                        {
                            key: 'service_fee_mode',
                            label: '是否收取服务费',
                            formItemKey: 'service_fee_mode',
                            componentClass: 'radio',
                            view: true,
                            required: true,
                            span: showData?.service_fee_mode === 1 ? 1 : 2,
                            elementProps: {
                                options: [
                                    {
                                        name: '是',
                                        value: 1
                                    },
                                    {
                                        name: '否',
                                        value: 0
                                    },
                                ]
                            }
                        },
                        {
                            key: 'service_fee_rate',
                            label: '服务费率',
                            formItemKey: 'service_fee_rate',
                            componentClass: 'inputNumber',
                            view: true,
                            required: true,
                            show: showData?.service_fee_mode === 1,
                            span: 1,
                            render: () => {
                                return <TaxRateInputNumber view={true} />
                            }
                        },
                        {
                            // TODO: 服务端展示字段
                            key: 'invoice_belong_area_name',
                            label: '发票国家/地区',
                            formItemKey: 'invoice_belong_area_name',
                            componentClass: 'input',
                            view: true,
                            span: 1,
                        },
                        {
                            key: 'is_oversea_invoice',
                            label: '是否海外INVOICE',
                            formItemKey: 'is_oversea_invoice',
                            componentClass: 'select',
                            view: true,
                            elementProps: {
                                options: [
                                  {
                                    name: '是',
                                    value: 1,
                                  },
                                  {
                                    name: '否',
                                    value: 0,
                                  },
                                ],
                              },
                            span: 1,
                        },
                        {
                            // TODO: 服务端展示字段
                            key: 'invoice_type_name',
                            label: '发票类型',
                            formItemKey: 'invoice_type_name',
                            componentClass: 'input',
                            view: true,
                            span: 2,
                        },
                        {
                            key: 'quote.program_desc',
                            label: '报价方案',
                            formItemKey: 'quote.program_desc',
                            componentClass: 'input',
                            view: true,
                            required: true,
                            show: bindData,
                            span: 2,
                        },
                        {
                            key: 'quote.$quote_price_no_tax',
                            label: '报价金额-优惠前(不含税)',
                            formItemKey: 'quote.$quote_price_no_tax',
                            className: cx('quote_price'),
                            componentClass: 'currencyType',
                            required: true,
                            view: true,
                            show: !bindData,
                            elementProps: {
                                selectDisabled: true
                            }
                        },
                        {
                            key: 'quote.$quote_price_with_tax',
                            label: '报价金额-优惠前(含税 + 服务费)',
                            className: cx('quote_price'),
                            formItemKey: 'quote.$quote_price_with_tax',
                            componentClass: 'currencyType',
                            required: true,
                            view: true,
                            show: !bindData,
                            elementProps: {
                                selectDisabled: true
                            }
                        },
                        {
                            key: 'quote.$discount_amount',
                            label: <span>优惠金额<br/>(含税 + 服务费)</span>,
                            className: cx('quote_price'),
                            formItemKey: 'quote.$discount_amount',
                            show: !bindData,
                            view: true,
                            span: 2,
                            render: () => {
                                return <DiscountAmount />
                            }
                        },
                        {
                            key: 'quote.$discount_quote_price_no_tax',
                            formItemKey: 'quote.$discount_quote_price_no_tax',
                            label: '报价金额-优惠后(不含税)',
                            className: cx('quote_price'),
                            componentClass: 'currencyType',
                            required: true,
                            show: !bindData,
                            view: true,
                            elementProps: {
                                selectDisabled: true
                            }
                        },
                        {
                            key: 'quote.$discount_quote_price_with_tax',
                            formItemKey: 'quote.$discount_quote_price_with_tax',
                            label: '报价金额-优惠后(含税 + 服务费)',
                            className: cx('quote_price'),
                            componentClass: 'currencyType',
                            required: true,
                            show: !bindData,
                            view: true,
                            elementProps: {
                                selectDisabled: true
                            }
                        },
                        {
                            key: 'quote.$last_round_quote_price',
                            formItemKey: 'quote.$last_round_quote_price',
                            label: `报价总金额-上轮(含税 + 服务费)`,
                            className: cx('quote_price'),
                            componentClass: 'currencyType',
                            required: true,
                            view: true,
                            show: !bindData && !!showData?.quote.$price_change,
                            span: 1
                        },
                        {
                            key: 'quote.$quote_price',
                            formItemKey: 'quote.$quote_price',
                            label: <span>报价总金额<br/>(含税 + 服务费)</span>,
                            componentClass: 'currencyType',
                            required: true,
                            view: true,
                            show: !bindData && !detail?.need_cooperate_account_supplier_settle_price,
                            span: showData?.quote.$price_change ? 1 : 2
                        },
                        {
                            key: showData?.quote.$price_change ? 'quote.$last_round_quote_price' : 'quote.$quote_price',
                            formItemKey: showData?.quote.$price_change ? 'quote.$last_round_quote_price' : 'quote.$quote_price',
                            label: <span>报价总金额-变更前<br/>(含税 + 服务费)</span>,
                            componentClass: 'currencyType',
                            className: cx('quote_price'),
                            required: true,
                            view: true,
                            show: detail?.need_cooperate_account_supplier_settle_price && !bindData,
                            span: 1,
                        },
                        {
                            key: 'quote.$quote_price',
                            formItemKey: 'quote.$quote_price',
                            label: <span>报价总金额-变更后<br/>(含税 + 服务费)</span>,
                            componentClass: 'currencyType',
                            required: true,
                            view: true,
                            show: detail?.need_cooperate_account_supplier_settle_price && !bindData,
                            span: 1,
                        },
                        {
                            key: 'quote.quote_attachments',
                            label: '报价明细',
                            formItemKey: 'quote.quote_attachments',
                            span: 2,
                            show: !bindData,
                            render: () => {
                                const quoteIndex = detail?.project_plan_info?.cooperate_accounts?.findIndex?.(v => v.index_id === index_id)
                                return <QuoteFileDetail
                                    detail={detail}
                                    view={true}
                                    quoteIndex={quoteIndex}
                                    supplierQuotationTableConfig={showData}
                                    cooperateAccountId={cooperate_account_id}
                                />;
                            }
                        },
                    ]}
                />
            </Form>
        </div>,
    })

    const openModal = useMemoizedFn((openConfig: QuotationDetailModalProps) => {
        setConfig({
            ...config,
            ...openConfig
        })
        open()
    })

    return {
        open: openModal,
        close: destroy
    }
}
